import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Observable, from, of, throwError } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import { errorMessages, errorSummaries } from '../configs/errors.config';
import { FormService, ErroValidacao } from '../services/form.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly messageService = inject(MessageService);
  private readonly formService = inject(FormService);


  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((response: HttpErrorResponse) => {
        const normalized$ = isJsonBlob(response.error)
          ? from(
              parseBlobToJson(response.error)
                .then((json) => ({ ...response, error: json } as HttpErrorResponse))
                .catch(() => response),
            )
          : of(response);

        return normalized$.pipe(
          tap((normalizedResponse) => this.handleError(normalizedResponse)),
          mergeMap((normalizedResponse) => throwError(() => normalizedResponse)),
        );
      }),
    );
  }

  private handleError(response: HttpErrorResponse): void {
    console.error('Erro bruto recebido pelo interceptor:', response);

    // Trata especificamente erro 400 com array de validações
    if (response.status === 400 && Array.isArray(response.error?.errors)) {
      this.handleValidationError(response);
    } else {
      this.handleGenericError(response);
    }
  }

  /**
   * Trata erro 400 com array de erros de validação.
   *
   * Extrai os erros da estrutura `{ errors: [{ campo, mensagem }, ...] }`
   * e os armazena no FormService para serem aplicados aos campos.
   */
  private handleValidationError(response: HttpErrorResponse): void {
    const erros: ErroValidacao[] = response.error.errors;

    // Armazena os erros no FormService
    this.formService.definirErrosValidacao(erros);

    // Exibe toast com resumo dos erros
    const camposComErro = erros.map(e => e.campo).join(', ');
    this.messageService.add({
      severity: 'warn',
      summary: response.error.message || 'Erro na validação',
      detail: `Campos com erro: ${camposComErro}`,
      life: 5000,
    });
  }

  /**
   * Trata erros genéricos (não relacionados a validação de formulário).
   */
  private handleGenericError(response: HttpErrorResponse): void {
    // Limpa erros de validação anteriores
    this.formService.limparErrosValidacao();

    const message = this.getErrorMessage(response);
    const summary = errorSummaries[response.status] || 'Erro';
    this.messageService.add({
      severity: this.getSeverityByStatus(response.status),
      summary: summary,
      detail: message,
      life: 5000,
    });
  }

  private getErrorMessage(response: HttpErrorResponse): string {
    if (typeof response.error === 'string') {
      try {
        const parsed = JSON.parse(response.error);
        return parsed?.message || response.error;
      } catch {
        return response.error;
      }
    } else if (response?.error?.message) {
      return response.error.message;
    } else {
      return response?.message || errorMessages[response.status];
    }
  }

  private getSeverityByStatus(status: number): 'info' | 'warn' | 'error' {
    if (status >= 500) return 'error';
    if (status >= 400) return 'warn';
    return 'info';
  }
}

// Função auxiliar para checar se é Blob JSON
function isJsonBlob(error: unknown): error is Blob {
  return error instanceof Blob && error.type === 'application/json';
}

// Função auxiliar para converter Blob em JSON
function parseBlobToJson(blob: Blob): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        resolve(JSON.parse(reader.result as string));
      } catch {
        reject();
      }
    };
    reader.onerror = reject;
    reader.readAsText(blob);
  });
}
